import React from "react";
import { Drawer, Button, Collapse, Typography, Avatar, Space } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { CaretDownFilled, UserOutlined, ProfileOutlined, LogoutOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Panel } = Collapse;

const MobileNavigation = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  showAuthModal,
  mainNavStructure,
  location,
  themeColor,
  user,
  userData,
  handleLogout,
}) => {
  const navigate = useNavigate();

  const renderMobileNavItems = () => {
    return mainNavStructure.map((nav) => {
      if (!nav.items) {
        return (
          <div key={nav.key} style={{ margin: "8px 0" }}>
            <Link
              to={nav.path}
              style={{
                color: location.pathname === nav.path ? "#480558" : "#480558",
                fontSize: "18px",
                fontWeight: 500,
                textDecoration: "none",
                display: "block",
                padding: "12px 24px",
                borderBottom: location.pathname === nav.path ? "2px solid #480558" : "none",
                textAlign: "left",
              }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {nav.label}
            </Link>
          </div>
        );
      }

      return (
        <Collapse
          key={nav.key}
          bordered={false}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <CaretDownFilled
              style={{
                fontSize: "14px",
                color: "#480558",
                transition: "transform 0.3s",
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          )}
          style={{
            background: "transparent",
            margin: "8px 0",
          }}
        >
          <Panel
            header={
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  color: location.pathname.startsWith(nav.pathPrefix)
                    ? "#480558"
                    : "#000",
                }}
              >
                {nav.label}
              </span>
            }
            key={nav.key}
            style={{
              border: "none",
              background: "transparent",
            }}
          >
            <div style={{ paddingLeft: "10px" }}>
              {nav.items.map((item) => (
                <div key={item.key} style={{ margin: "8px 0" }}>
                  <Link
                    to={item.path}
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: location.pathname === item.path ? "#480558" : "#000",
                      textDecoration: "none",
                      display: "block",
                      padding: "8px 24px",
                      borderLeft: location.pathname === item.path ? "3px solid #480558" : "none",
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}
            </div>
          </Panel>
        </Collapse>
      );
    });
  };

  const renderAuthSection = () => {
    if (user) {
      return (
        <div style={{ padding: '20px 0', borderTop: '1px solid #f0f0f0' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* User Info */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Avatar 
                size="large" 
                icon={<UserOutlined />}
                src={userData?.photoURL}
                style={{ 
                  backgroundColor: themeColor, 
                  marginBottom: '8px',
                  border: `2px solid ${themeColor}`
                }}
              />
              <div>
                <Text strong style={{ fontSize: '16px', display: 'block' }}>
                  {userData?.displayName || user.email?.split('@')[0] || 'User'}
                </Text>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {user.email}
                </Text>
              </div>
            </div>

            {/* Profile Button */}
            <Button 
              type="primary" 
              block 
              icon={<ProfileOutlined />}
              onClick={() => {
                navigate('/profile');
                setMobileMenuOpen(false);
              }}
              style={{
                height: '45px',
                fontSize: '16px',
                marginBottom: '8px'
              }}
            >
              My Profile
            </Button>

            {/* Logout Button */}
            <Button 
              block 
              danger
              icon={<LogoutOutlined />}
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              style={{
                height: '45px',
                fontSize: '16px'
              }}
            >
              Logout
            </Button>
          </Space>
        </div>
      );
    } else {
      return (
        <div style={{ padding: '20px 0', borderTop: '1px solid #f0f0f0' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              type="primary" 
              block 
              onClick={() => {
                showAuthModal(true);
                setMobileMenuOpen(false);
              }}
              style={{
                height: '45px',
                fontSize: '16px',
                marginBottom: '8px'
              }}
            >
              Sign Up
            </Button>
            <Button 
              block 
              onClick={() => {
                showAuthModal(false);
                setMobileMenuOpen(false);
              }}
              style={{
                height: '45px',
                fontSize: '16px',
                borderColor: themeColor,
                color: themeColor
              }}
            >
              Log in
            </Button>
          </Space>
        </div>
      );
    }
  };

  const mobileMenu = (
    <div style={{ padding: "20px 0" }}>
      {/* Navigation Items */}
      {renderMobileNavItems()}
      
      {/* Auth Section */}
      {renderAuthSection()}
    </div>
  );

  return (
    <Drawer
      title={
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <Text strong style={{ color: themeColor, fontSize: "20px" }}>
            Yalla Nellab
          </Text>
        </div>
      }
      placement="right"
      onClose={() => setMobileMenuOpen(false)}
      open={mobileMenuOpen}
      width={300}
      styles={{
        body: { padding: "0 10px" },
        header: { padding: "10px 16px" },
      }}
      className="mobile-navigation-drawer"
    >
      {mobileMenu}

      {/* Add custom CSS to ensure proper alignment */}
      <style jsx>{`
        :global(.mobile-navigation-drawer .ant-collapse-header) {
          padding: 12px 24px !important;
          display: flex !important;
          align-items: center !important;
        }
        
        :global(.mobile-navigation-drawer .ant-collapse-header-text) {
          flex: 1 !important;
          text-align: left !important;
        }
        
        :global(.mobile-navigation-drawer .ant-collapse-expand-icon) {
          margin-left: auto !important;
          padding-left: 16px !important;
        }

        :global(.mobile-navigation-drawer .ant-btn-block) {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        :global(.mobile-navigation-drawer .ant-btn .anticon) {
          margin-right: 8px !important;
        }
      `}</style>
    </Drawer>
  );
};

export default MobileNavigation;